import { Checkbox, Text } from "@ttc3k/trekker";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Box, Flex, FlexProps } from "styled-system/jsx";
import type { BusinessScoped } from "@shared/businessScope";

type BusinessScopedFieldGroupProps = {
  /** Businesses chosen for the purchase / submission, in order. */
  businesses: BusinessScoped[];
  checkedOperatorIds: string[];
  onCheckedChange: (operatorId: string, checked: boolean) => void;
  /** Fields for one business; omit (or return null) when there are none. */
  renderFields?: (operatorId: string) => ReactNode;
  gap?: FlexProps["gap"];
};

/**
 * A per-business add-on or toggle: pick which of the chosen businesses it
 * applies to, then answer its fields once for each of them.
 */
export const BusinessScopedFieldGroup = ({
  businesses,
  checkedOperatorIds,
  onCheckedChange,
  renderFields,
  gap = "200",
}: BusinessScopedFieldGroupProps) => {
  const { t } = useTranslation("booker", {
    keyPrefix: "QUESTIONNAIRE.BUSINESS_SCOPE",
  });

  if (businesses.length === 0) {
    return (
      <Text visual="smallRegular" color="text.mid">
        {t("NO_BUSINESSES")}
      </Text>
    );
  }

  const checked = new Set(checkedOperatorIds);
  const label = (b: BusinessScoped) => b.businessName || b.operatorId;

  return (
    <Flex flexDirection="column" gap={gap}>
      {businesses.length > 1 && (
        <Flex flexDirection="column" gap="100">
          <Text visual="smallMedium" color="text.dark">
            {t("WHICH_BUSINESSES")}
          </Text>
          {businesses.map(b => (
            <Checkbox
              key={b.operatorId}
              label={label(b)}
              checked={checked.has(b.operatorId)}
              onCheckedChange={e => onCheckedChange(b.operatorId, e.checked === true)}
            />
          ))}
        </Flex>
      )}
      {businesses
        .filter(b => checked.has(b.operatorId))
        .map(b => {
          const fields = renderFields?.(b.operatorId);
          if (!fields) return null;
          return (
            <Flex
              key={b.operatorId}
              flexDirection="column"
              gap={gap}
              pl={businesses.length > 1 ? "200" : undefined}
              borderLeft={businesses.length > 1 ? "2px solid" : undefined}
              borderColor="border.light"
            >
              {businesses.length > 1 && (
                <Box>
                  <Text visual="smallSemiBold" color="text.dark">
                    {t("FOR_BUSINESS", { name: label(b) })}
                  </Text>
                </Box>
              )}
              {fields}
            </Flex>
          );
        })}
    </Flex>
  );
};
